import { WizConstants } from "../../model/constants";
import { FeiKeyDef } from "../../vo/workingdata/fei-key-definition.obj";
import { FeiVO } from "../../vo/workingdata/fei.vo";
import { WidgetCell, WidgetLine } from "../../vo/workingdata/widget-line.obj";
import { SisterResult } from "../../model/interfaces";

// couple date début / fin
export interface CompareRange {
    start: number;
    end: number;
}

// pour une widgetLine, couple clé d'ensemble / clé d'unicité
export interface CompareKey {
    groupKey: string;
    unicityKey: string;
}

/**
 * création des packs de comparaison pour COMPARERANGE & FALSETWINS
 */
export class CompareManager {

  /**
   * construction d'une clé de série sur la WidgetLine
   * @param line 
   * @param groupFKDs les champs qui servent pour le groupe
   * @param identityFKDs les champs qui servent pour l'identité
   * @param measureFullKey 
   * @returns CompareKey, la clé groupe / unicité
   */
  private static feedPackKey(line: WidgetLine, groupFKDs: FeiKeyDef[], identityFKDs: FeiKeyDef[], measureFullKey: string): CompareKey {
    if(!line.packKeyMap?.has(measureFullKey)) { // pas encore construite pour cette série, création
        // je crée ma clé wildCard
        if (!line.bl.splittedKey) {
            line.bl.splittedKey = line.bl.key.split(WizConstants.KEY_SEPARATOR);
        }
        let myGroupPart: string[] = [];
        groupFKDs.forEach((fkd: FeiKeyDef) => {
            myGroupPart.push(line.bl.splittedKey[fkd.indexInKey]);
        });

        let myIdentityPart: string[] = [];
        identityFKDs.forEach((fkd: FeiKeyDef) => {
            myIdentityPart.push(line.bl.splittedKey[fkd.indexInKey]);
        }); 

        if(!line.packKeyMap) {
            line.packKeyMap = new Map();
        }
        line.packKeyMap.set(measureFullKey, 
            {groupKey: myGroupPart.join(WizConstants.KEY_SEPARATOR), unicityKey: myIdentityPart.join(WizConstants.KEY_SEPARATOR) }
        );     
    }
    
    // ma clé de sister avec son *
    return line.packKeyMap.get(measureFullKey);
  }

    /**
     * construction du pack
     * @param measureFullKey la clé de série du pack
     * @param measureKey la mesure qu'on est en train de calculer
     * @param fei 
     * @param groupFKD ce qui constituent la clé de l'ensemble
     * @param binKey éventuel complément binaire, pour gérer des groupes / sous-groupes identiques
     * @param identityFKD ce qui fait l'unicité d'un élément
     * @param startField 
     * @param endField 
     * @param twinFKD mesure de comparaison dans le cas d'un falseTwin
     */
    public static generatePack(measureFullKey: string, measureKey: string, 
        fei: FeiVO, groupFKD: FeiKeyDef[], binKey: string, identityFKD: FeiKeyDef[], startField: string, endField: string, twinFKD?: FeiKeyDef): void {

        // TODO TODO ajout des écouteurs sur les FDV comme Keyval
        
        // les dates / début-fin sous forme CompareRange
        const rangeSet: CompareRange[] = []; 
        // prise des lignes du FEI
        const lines: WidgetLine[] = fei.getAllLines();

        // ### construction de la ligne temporelle



        const tmpSetStart: Set<number> = new Set();
        const tmpSetEnd: Set<number> = new Set();
        let startTime: string;
        let endTime: string;
        lines.forEach((wl: WidgetLine) => {
            startTime = fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.bl.scen, startField);
            endTime = fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.bl.scen, endField);
            // prise des valeurs de dates
            tmpSetStart.add(Number(startTime));
            tmpSetEnd.add(Number(endTime));
        });

        // toutes les dates, début et fin dans l'ordre. Va déterminer l'ensemble des plages possibles
        const globalSet: Set<number> = new Set([...tmpSetStart, ...tmpSetEnd]);
        // on ordonne les dates
        const globalArr: number[] = [...globalSet];
        globalArr.sort((a, b) => a - b);

        for (let i = 0; i < globalArr.length; i++) {
            let newStart: number = globalArr[i]; // on ajoute aussi les dates uniques
            rangeSet.push({start:newStart, end: newStart});
            if( i < globalArr.length -1) {
              let newEnd: number = globalArr[i + 1];
              rangeSet.push({start:newStart, end: newEnd});
            }  
        }

        // la map de résultat pour alimenter SisterResult.resultMap
        let sistersVals: Map<WidgetLine, string> = new Map();
        
        // les cellules qui porteront les résultats des calculs
        let sisterCells: WidgetCell[] = [];

        // les éléments pour calcul
        // construction des groupes (packKey | binKey) => range[]  => WL[]
        const groupMap: Map<string, Map<CompareRange, WidgetLine[]>> = new Map();

        // construction de la / des clés field et unicité pour chaque WL, et mémorisation
        let linePackKey: CompareKey;
        let lineBinKey: number[]; // la partie binaire de la clé
        let fullKey: string; // la clé complète pack + bin
        let cell: WidgetCell;
        let start: number; // date de début pour la wl
        let end: number; // date de fin pour la wl
        let twinValue: string = null; // la valeur de comparaison pour falseTwin
        lines.forEach((wl: WidgetLine) => {    

            // alimentation de la map de résultats du set
            sistersVals.set(wl, '0'); // par défaut, pas de collision
            // alimentation des cellules
            cell = fei.model.cellMatrixEngine.getWidgetCell(wl, measureKey);
            sisterCells.push(cell);

            linePackKey = this.feedPackKey(wl, groupFKD, identityFKD, measureFullKey);
            lineBinKey = []; 
            if (binKey) { // ventilation éventuelle des clés binaires
                const binVal: number = Number(fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.bl.scen, binKey));
                if (!isNaN(binVal)) {
                  const binStr: string[] = binVal.toString(2).split('').reverse();
        
                  for (let j = 0; j < binStr.length; j++) {
                    const binPart: number = Number(binStr[j]);
                    if (binPart === 1) {
                        lineBinKey.push(Math.pow(2, j));
                    }
                  }
                }
            }
            if(lineBinKey.length === 0) {
                lineBinKey.push(0);
            }

            // en cas de falseTwin, il faut que les clés soient divergentes
            if(twinFKD) {
                twinValue = fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.bl.scen, twinFKD.fullCode);
            } 

            start = Number(fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.bl.scen, startField));
            end = Number(fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.bl.scen, endField));

            if (!!start && !!end) {  
                // détermination des ranges de la WL, on exclut les hors zones
                let realRange: CompareRange[] = rangeSet.filter(item => !(item.end < start && item.start > end));
                let activeRange: CompareRange[] = [];
                realRange.forEach((cr: CompareRange) => {
                    if (cr.start >= start && cr.end <= end) { // je possède cette plage
                        activeRange.push(cr);
                    } 
                });

                if(activeRange.length > 0) {
                    // construction des clés d'appartenance pour la WidgetLine (group + bin)
                    lineBinKey.forEach((bin: number) => {
                        fullKey = [linePackKey.groupKey, bin].join(WizConstants.KEY_SEPARATOR);
                        if(!groupMap.has(fullKey)) {
                            groupMap.set(fullKey, new Map());
                        }

                        // pour chaque range
                        activeRange.forEach((cr: CompareRange) => {
                            if(!groupMap.get(fullKey).has(cr)) {
                                groupMap.get(fullKey).set(cr, []);
                            }
                            else { // sinon, y'a un problème (peut-être, pas en cas de FalseTwin si les valeurs sont les mêmes)
                            
                                let doError: boolean = true; // par défaut, c'est une erreur

                                if(!!twinFKD) { // mais pas forcément en cas de falseTwin si la valeur est la même
                                    let existWls: WidgetLine[] = [...groupMap.get(fullKey).get(cr)];
                                    let valSet: Set<string> = new Set();
                                    existWls.forEach(existWl => {
                                        valSet.add(fei.model.cellMatrixEngine.getCellValue(existWl.level, existWl.key, existWl.bl.scen, twinFKD.fullCode));
                                    });
                                    valSet.add(fei.model.cellMatrixEngine.getCellValue(wl.level, wl.key, wl.bl.scen, twinFKD.fullCode));
                                    if(valSet.size > 1) {
                                        doError = false;
                                    }

                                    // if(wl.key === "PLAN2|PLAN2_102|DLA|WIZ|WIZ-100") {
                                    //     console.log('twinValue',existWlValue,  twinValue,doError);
                                    // }
                                } 
                                if(doError) { // mode compare
                                    // j'ajoute tout le set existant ET la ligne en cours en erreur
                                    [...groupMap.get(fullKey).get(cr).values()].forEach((twin: WidgetLine) => {
                                        sistersVals.set(twin, '1');
                                    });
                                    sistersVals.set(wl, '1');
                                }
                             }
                            groupMap.get(fullKey).get(cr).push(wl);
                        });   
                    });
                }
            }
        });


        let sisterResult: SisterResult = {
            l: fei.visionStepLevel, sc: fei.scenario, magg: measureFullKey, sk: WizConstants.wildcardDomain,
            rMap: sistersVals, // dans ce cas, on alimente la map, pas le result 
            count: lines.length, sis: sisterCells, mk: measureKey };
        
        // la map complète pour cette série
        let sistersMap: Map<string, SisterResult> = new Map();
      
        sistersMap.set(WizConstants.wildcardDomain, sisterResult);
        // et on alimente la matrice
        fei.model.cellMatrixEngine.setNeoSistersDependencies(fei.visionStepLevel, fei.scenario, measureFullKey, sistersMap);
        // console.log('compare', measureFullKey, twinFKD);
    }

    
}
